import { Formik, FormikProps } from 'formik';
import { useMemo } from 'react';
import * as Yup from 'yup';
import { Modal } from '@shared/components/modals/Modal';
import { ModalContentContainer } from '@shared/styledComponents';
import { StateRegulationForm } from './StateRegulationForm';
import { IStateRegulationModel, IIStateRegulationFormModel } from './interfaces';

interface IStateRegulationModalProps {
  onClose: () => void;
  onSubmit: (values: IIStateRegulationFormModel) => void;
  stateRegulation: IStateRegulationModel | null;
  stateRegulationModalPosition: number;
}

const validationSchema = Yup.object().shape({
  salesTaxInformation: Yup.object().shape({
    plainText: Yup.string().required('Sales Tax Information can not be empty'),
  }),
  specificRequirements: Yup.object().shape({
    plainText: Yup.string().required('Specific Requirements can not be empty'),
  }),
  titleDocuments: Yup.object().shape({
    plainText: Yup.string().required('Title Documents can not be empty'),
  }),
});

export const yesNoNotApplicableOptions = [
  { id: 'N/A', value: null },
  { id: 'Yes', value: true },
  { id: 'No', value: false },
];

export const mapOptionTypeIdToValue = (optionTypeId: string) => {
  return yesNoNotApplicableOptions.find((option) => option.id === optionTypeId)?.value ?? null;
};

const mapValueToOptionTypeId = (value: boolean | null) => {
  if (typeof value === 'boolean') {
    const type = yesNoNotApplicableOptions.find((option) => option.value === value);

    if (type) {
      return type.id;
    }
  }

  return yesNoNotApplicableOptions[0].id;
};

export const StateRegulationModal = ({ onClose, onSubmit, stateRegulation, stateRegulationModalPosition }: IStateRegulationModalProps) => {
  const initialValues = useMemo<IIStateRegulationFormModel | null>(() => {
    if (!stateRegulation) {
      return null;
    }

    const {
      addStatus,
      dropStatus,
      isOutOfStateTitleAllowed,
      notaryRequiredStatus,
      salesTaxInformation,
      sorRequiredStatus,
      titleHoldingStatus,
      specificRequirements,
      titleDocuments,
      ...restValues
    } = stateRegulation;

    return {
      ...restValues,
      addStatus: mapValueToOptionTypeId(addStatus),
      dropStatus: mapValueToOptionTypeId(dropStatus),
      isOutOfStateTitleAllowed: mapValueToOptionTypeId(isOutOfStateTitleAllowed),
      notaryRequiredStatus: mapValueToOptionTypeId(notaryRequiredStatus),
      sorRequiredStatus: mapValueToOptionTypeId(sorRequiredStatus),
      titleHoldingStatus: mapValueToOptionTypeId(titleHoldingStatus),
      salesTaxInformation: {
        html: salesTaxInformation,
        plainText: salesTaxInformation,
      },
      specificRequirements: {
        html: specificRequirements,
        plainText: specificRequirements,
      },
      titleDocuments: {
        html: titleDocuments,
        plainText: titleDocuments,
      },
    };
  }, [stateRegulation]);

  if (!initialValues) {
    return null;
  }

  return (
    <Modal
      hideBackdrop
      title="Edit State Regulation"
      open={!!stateRegulation} 
      resetPosition={stateRegulationModalPosition}
      onClose={onClose}
    >
      <ModalContentContainer style={{ maxWidth: 1200 }}>
        <Formik initialValues={initialValues} validationSchema={validationSchema} onSubmit={onSubmit}>
          {({ ...props }: FormikProps<IIStateRegulationFormModel>) => (
            <StateRegulationForm {...props} onClose={onClose} />
          )}
        </Formik>
      </ModalContentContainer>
    </Modal>
  );
};
